import type { InjectionKey, Ref } from 'vue'
import type { InstantRangeValue, RollingSpan } from '@hantera/design-system'
import type {
  JobSpanDetail,
  RequestDetail,
  SendingSpanDetail,
  TraceDetail,
  TraceStatus,
  TraceSummary,
} from '@/backend/traces.types'

export interface DateRange {
  from: Date
  to: Date
}

/** What narrows the list. Mutable, so the toolbar can bind to it. */
export interface TracesFilters {
  /** Resource terms, ANDed by the API across the whole trace. */
  resources: string[]
  traceId: string | null
  status: TraceStatus | null
}

/**
 * The Traces tab's view state: the list, its filters, the open trace and its fetched payloads.
 *
 * A view context rather than a Pinia store because every field here is per-view. Two Monitoring
 * views open side by side must be able to hold two different date ranges and two different open
 * traces; a singleton store would make them fight over one.
 */
export interface TracesViewContext {
  readonly items: TraceSummary[]

  /**
   * Whether another page exists, as reported by the server. Never derived from `items.length`:
   * a page can be exactly full and still be the last, and `DataTable` will poll `loadMore()`
   * without pause for as long as this stays true.
   */
  readonly hasMore: boolean

  readonly filters: TracesFilters

  /**
   * The time window by which the list is bounded. Null means "all time" — no bound — which is what a
   * deep link into a specific order or job arrives with, so its trace is found however old it is.
   */
  readonly dateRange: DateRange | null

  /**
   * The span the window was derived from, when it ends at "now". Null for a fixed span.
   *
   * Auto-refresh is only meaningful while this is set: a fixed range ends in the past, so polling it
   * can turn up nothing but traces that were written late.
   */
  readonly rolling: RollingSpan | null

  readonly trace: TraceDetail | null

  /**
   * The selected span of the trace being viewed, addressed as `{kind}:{spanId}` where the span has a
   * row id and by its span key otherwise — the same form `?span=` takes.
   *
   * The addressable form rather than the span key, because a span key is a resource path that a
   * retention pass can expire while a bookmark to the row id survives.
   *
   * Held here rather than in the detail component because the detail is mounted by
   * `ListDetailLayout`, which passes no listeners — a component keyed on the trace id has nowhere to
   * emit a span change to.
   *
   * `undefined` while the selection belongs to another trace, so a span never leaks across traces.
   */
  readonly openSpan: string | undefined

  get listState(): Readonly<{
    isLoading: boolean
    error: string | null
  }>

  get traceState(): Readonly<{
    isLoading: boolean
    error: string | null
  }>

  /** Discards the cursor and replaces the list with the first page. */
  refresh(): Promise<void>

  /** Appends the next page. A no-op once `hasMore` is false. */
  loadMore(): Promise<void>

  /**
   * Advances a rolling window and merges whatever is new into the head of the list, leaving the
   * pages already loaded — and the operator's scroll position and selection — in place.
   *
   * A no-op for a fixed range. Replacing the list on a timer, as `refresh()` does, would drop
   * someone scrolled five pages deep back to the first fifty rows every interval.
   */
  pollNewer(): Promise<void>

  setRange(range: InstantRangeValue | null): void

  /** A bare UUID becomes the trace id; anything else becomes a resource term. */
  addTerm(term: string): void
  removeTerm(term: string): void
  clearTraceId(): void

  loadTrace(traceId: string): Promise<void>

  /**
   * Selects a span within a named trace.
   *
   * The trace id is a parameter rather than implied by the open trace, so a span can be addressed
   * before that trace has finished loading — which is how a deep link arrives.
   */
  selectSpan(traceId: string, span: string | undefined): void

  /** Fetches a span's payload, caching the outcome — including its absence. */
  loadRequest(traceId: string, requestId: string): Promise<void>

  /**
   * Fetches any response frames that have arrived since the last fetch and appends them.
   *
   * Separate from {@link loadRequest}, which caches absence and never re-asks: a stream's row is
   * written when the connection opens, so its frame count keeps growing while it runs and the cached
   * detail would otherwise be frozen at whatever had arrived by the first click.
   */
  loadMoreFrames(traceId: string, requestId: string): Promise<void>

  /** `undefined` means not yet asked; `null` means asked, and there is nothing. */
  requestDetail(requestId: string): RequestDetail | null | undefined
  isLoadingRequest(requestId: string): boolean

  /**
   * Job and sending payloads, which are durable rows rather than captured traffic — so they exist
   * regardless of any request log level, and are authorized against their own family.
   */
  loadJobSpan(traceId: string, jobId: string): Promise<void>
  loadSendingSpan(traceId: string, sendingId: string): Promise<void>

  jobSpanDetail(jobId: string): JobSpanDetail | null | undefined
  sendingSpanDetail(sendingId: string): SendingSpanDetail | null | undefined
  isLoadingSpan(spanId: string): boolean
}


export const tracesViewContextKey: InjectionKey<TracesViewContext> = Symbol('tracesViewContext')

/**
 * A trace a cross-link asked to open, adopted by `TracesTab` as its initial selection.
 *
 * Separate from {@link TracesViewContext} because it is an instruction rather than state: the context
 * describes what the list holds, while this says what to open once, on arrival.
 */
export const openTraceIdKey: InjectionKey<Ref<string | undefined>> = Symbol('openTraceId')

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * A bare UUID is a trace id; anything else is a resource path.
 *
 * No further inference is needed: a resource path names its own kind in its first segment
 * (`actors/…`, `egresses/…`, `jobs/…`), so there is nothing for the UI to guess.
 */
export function isTraceId(term: string): boolean {
  return UUID.test(term.trim())
}
